import React, { useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import {
  buildSiteJsonLdSchemas,
  buildOrganizationSchema,
  buildRealEstateAgentSchema,
  resolveTwitterSiteHandle,
} from '../utils/seo/schemaBuilders';
import {
  truncateSeoTitle,
  truncateSeoDescription,
  resolveOfficialBaseUrl,
  normalizeProductionUrl,
  inferOgImageType,
  getOgImageDimensions,
  buildDynamicPropertyDescription,
  resolveHomeOgImage,
} from '../utils/seo/metaHelpers';
import { buildPropertyUrl } from '../utils/seo/urlHelpers';
import { buildEnhancedPropertyJsonLd } from '../utils/seo/propertySchema';
import { resolveSeoRobots } from '../utils/seo/robotsPolicy';
import { firebaseMeasurementId } from '../firebase';

const SEO = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  canonicalUrl,
  pageType = 'website',
  propertySeo = null,
  /** Dados do imóvel para meta description dinâmica e JSON-LD enriquecido */
  propertyData = null,
  /** Corretor responsável { name, telephone, email, url } */
  brokerInfo = null,
  twitterCard = 'summary_large_image',
  twitterTitle = null,
  twitterDescription = null,
  twitterImage = null,
  twitterImageAlt = null,
  schemaData = null,
  supplementarySchemas = [],
  paginationLinks = null,
  includeSiteSchemas = false,
  includeBlogRss = false,
  articleMeta = null,
  robots = null,
  locale = 'pt_BR',
}) => {
  const settings = useCompanySettings();
  const { allowsAnalytics, allowsMarketing } = useCookieConsent();
  const officialBaseUrl = resolveOfficialBaseUrl(settings);
  const externalGoogleAnalyticsId = useMemo(() => {
    const configuredId = String(settings.seoGoogleAnalytics || '').trim();
    if (!configuredId || configuredId === firebaseMeasurementId) return '';
    return configuredId;
  }, [settings.seoGoogleAnalytics]);
  const companyName = (settings.companyName || '').trim();
  const brand = companyName || 'Imobiliária';
  const isAwaitingBrand = !companyName && !settings.settingsReady;
  const robotsFinal = resolveSeoRobots(propertyData, robots);

  const rawTitle =
    propertySeo?.metaTitle ||
    title ||
    settings.seoMetaTitle ||
    (isAwaitingBrand ? 'Imobiliária - Imóveis' : `${brand} - Imóveis`);

  const rawDescription = useMemo(() => {
    if (propertySeo?.metaDescription?.trim()) return propertySeo.metaDescription;
    if (propertyData) return buildDynamicPropertyDescription(propertyData, brand);
    return (
      description ||
      settings.seoMetaDescription ||
      'Encontre os melhores imóveis. Especialistas em compra, venda e aluguel.'
    );
  }, [propertySeo, propertyData, description, settings.seoMetaDescription, brand]);

  const metaTitle = truncateSeoTitle(rawTitle);
  const metaDescription = truncateSeoDescription(rawDescription);

  useEffect(() => {
    if (metaTitle) {
      document.title = metaTitle;
    }
  }, [metaTitle]);

  const metaKeywords =
    propertySeo?.metaKeywords ||
    keywords ||
    settings.seoKeywords ||
    'imóveis, corretores, compra, venda, aluguel';

  const ogTitleFinal = truncateSeoTitle(
    propertySeo?.ogTitle || ogTitle || settings.seoOgTitle || rawTitle,
    95
  );
  const ogDescriptionFinal = truncateSeoDescription(
    propertySeo?.ogDescription || ogDescription || settings.seoOgDescription || rawDescription,
    200
  );

  const ogImageRaw =
    propertySeo?.ogImage ||
    ogImage ||
    (!propertyData ? resolveHomeOgImage(settings) : null) ||
    settings.seoOgImage ||
    settings.logo ||
    propertyData?.coverImage ||
    (propertyData?.images?.length ? propertyData.images[0] : null);

  const ogImageFinal = ogImageRaw
    ? normalizeProductionUrl(ogImageRaw, officialBaseUrl)
    : null;

  const canonicalUrlFinal = normalizeProductionUrl(
    (propertyData?.id
      ? buildPropertyUrl(propertyData, officialBaseUrl)
      : null) ||
      propertySeo?.canonicalUrl ||
      canonicalUrl ||
      settings.seoCanonicalUrl ||
      (typeof window !== 'undefined' ? window.location.href : officialBaseUrl),
    officialBaseUrl
  );

  const ogTypeFinal = propertySeo?.ogType || pageType;
  const twitterCardFinal = propertySeo?.twitterCard || twitterCard;
  const twitterTitleFinal = truncateSeoTitle(
    propertySeo?.twitterTitle || twitterTitle || ogTitleFinal,
    70
  );
  const twitterDescriptionFinal = truncateSeoDescription(
    propertySeo?.twitterDescription || twitterDescription || ogDescriptionFinal,
    200
  );
  const twitterImageFinal = normalizeProductionUrl(
    propertySeo?.twitterImage || twitterImage || ogImageFinal,
    officialBaseUrl
  );

  const twitterImageAltFinal =
    twitterImageAlt ||
    propertySeo?.ogTitle ||
    propertySeo?.schemaName ||
    metaTitle;

  const ogImageAltFinal = truncateSeoDescription(
    propertySeo?.ogTitle || ogTitleFinal || metaTitle,
    420
  );

  const ogImageMeta = ogImageFinal
    ? {
        type: inferOgImageType(ogImageFinal),
        ...getOgImageDimensions(ogImageFinal),
      }
    : null;

  const twitterSite = resolveTwitterSiteHandle(settings);
  const faviconHref = settings.favicon || '/favicon.ico';
  const appleTouchIcon = settings.favicon || '/logo192.png';

  const baseUrl = officialBaseUrl;

  const mainPropertySchema = useMemo(() => {
    if (schemaData) return schemaData;
    if (!propertyData && !propertySeo?.schemaType) return null;
    return buildEnhancedPropertyJsonLd({
      propertySeo,
      propertyData,
      settings,
      canonicalUrl: canonicalUrlFinal,
      metaTitle,
      metaDescription,
      ogImage: ogImageFinal,
      baseUrl,
      brokerInfo: brokerInfo || (propertyData?.brokerName
        ? {
            name: propertyData.brokerName,
            telephone: propertyData.brokerPhone,
            email: propertyData.brokerEmail,
          }
        : null),
    });
  }, [
    schemaData,
    propertySeo,
    propertyData,
    settings,
    canonicalUrlFinal,
    metaTitle,
    metaDescription,
    ogImageFinal,
    baseUrl,
    brokerInfo,
  ]);

  const allSupplementary = useMemo(() => {
    const list = [...(supplementarySchemas || [])];
    if (includeSiteSchemas) {
      list.push(...buildSiteJsonLdSchemas(settings, baseUrl));
    }
    return list.filter(Boolean);
  }, [supplementarySchemas, includeSiteSchemas, settings, baseUrl]);

  const rssUrl = normalizeProductionUrl(`${baseUrl}/feed/imoveis.xml`, baseUrl);
  const blogRssUrl = normalizeProductionUrl(`${baseUrl}/feed/blog.xml`, baseUrl);
  const paginationPrev = paginationLinks?.prev
    ? normalizeProductionUrl(paginationLinks.prev, officialBaseUrl)
    : null;
  const paginationNext = paginationLinks?.next
    ? normalizeProductionUrl(paginationLinks.next, officialBaseUrl)
    : null;

  const hasRecaptcha = Boolean(String(settings.recaptchaSiteKey || '').trim());

  return (
    <Helmet prioritizeSeoTags>
      <html lang="pt-BR" />
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      {metaKeywords && <meta name="keywords" content={metaKeywords} />}
      <meta
        name="google-site-verification"
        content="Q_a3k5VUBwg2qgehac6LT23ZTBhYse17QB9DxUOLYhs"
      />
      <meta name="robots" content={robotsFinal} />
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      <meta name="color-scheme" content="light" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={brand} />
      <link rel="canonical" href={canonicalUrlFinal} />

      <link rel="icon" href={faviconHref} />
      <link rel="shortcut icon" href={faviconHref} />
      <link rel="apple-touch-icon" href={appleTouchIcon} />

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://firebasestorage.googleapis.com" />
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      <link rel="dns-prefetch" href="https://maps.googleapis.com" />
      <link rel="dns-prefetch" href="https://maps.gstatic.com" />
      {hasRecaptcha && (
        <>
          <link rel="preconnect" href="https://www.google.com" />
          <link rel="dns-prefetch" href="https://www.gstatic.com" />
        </>
      )}

      <link rel="alternate" type="application/rss+xml" title="Imóveis" href={rssUrl} />
      {includeBlogRss && (
        <link rel="alternate" type="application/rss+xml" title="Blog" href={blogRssUrl} />
      )}

      <meta property="og:type" content={ogTypeFinal} />
      <meta property="og:title" content={ogTitleFinal} />
      <meta property="og:description" content={ogDescriptionFinal} />
      <meta property="og:url" content={canonicalUrlFinal} />
      <meta property="og:locale" content={locale} />
      <meta property="og:site_name" content={brand} />
      {ogImageFinal && <meta property="og:image" content={ogImageFinal} />}
      {ogImageFinal && ogImageMeta && (
        <>
          <meta property="og:image:width" content={String(ogImageMeta.width)} />
          <meta property="og:image:height" content={String(ogImageMeta.height)} />
          <meta property="og:image:type" content={ogImageMeta.type} />
          <meta property="og:image:alt" content={ogImageAltFinal} />
        </>
      )}
      {articleMeta?.publishedTime && (
        <meta property="article:published_time" content={articleMeta.publishedTime} />
      )}
      {articleMeta?.modifiedTime && (
        <meta property="article:modified_time" content={articleMeta.modifiedTime} />
      )}
      {articleMeta?.section && (
        <meta property="article:section" content={articleMeta.section} />
      )}
      {(articleMeta?.tags || []).map((tag) => (
        <meta key={`article-tag-${tag}`} property="article:tag" content={tag} />
      ))}

      <meta name="twitter:card" content={twitterCardFinal} />
      <meta name="twitter:url" content={canonicalUrlFinal} />
      <meta name="twitter:title" content={twitterTitleFinal} />
      <meta name="twitter:description" content={twitterDescriptionFinal} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      {twitterImageFinal && <meta name="twitter:image" content={twitterImageFinal} />}
      {twitterImageFinal && twitterImageAltFinal && (
        <meta name="twitter:image:alt" content={truncateSeoDescription(twitterImageAltFinal, 420)} />
      )}

      {paginationPrev && <link rel="prev" href={paginationPrev} />}
      {paginationNext && <link rel="next" href={paginationNext} />}

      {allowsAnalytics && externalGoogleAnalyticsId && (
        <>
          <script async src={`https://www.googletagmanager.com/gtag/js?id=${externalGoogleAnalyticsId}`} />
          <script>
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${externalGoogleAnalyticsId}');
            `}
          </script>
        </>
      )}

      {allowsAnalytics && settings.seoGoogleTagManager && (
        <>
          <script>
            {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${settings.seoGoogleTagManager}');
            `}
          </script>
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${settings.seoGoogleTagManager}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
              title="Google Tag Manager"
            />
          </noscript>
        </>
      )}

      {allowsMarketing && settings.seoFacebookPixel && (
        <script>
          {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${settings.seoFacebookPixel}');
            fbq('track', 'PageView');
          `}
        </script>
      )}

      {mainPropertySchema && (
        <script type="application/ld+json">{JSON.stringify(mainPropertySchema)}</script>
      )}

      {!mainPropertySchema && settings.seoBusinessType && !includeSiteSchemas && (
        <>
          <script type="application/ld+json">
            {JSON.stringify(buildOrganizationSchema(settings, baseUrl))}
          </script>
          <script type="application/ld+json">
            {JSON.stringify(buildRealEstateAgentSchema(settings, baseUrl))}
          </script>
        </>
      )}

      {allSupplementary.map((schema, i) => (
        <script key={`supplementary-schema-${i}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
